import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { track } from '@/lib/seo';
import { BUSINESS } from '@/data/site';

const CartContext = createContext(null);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem('mg_cart')) || [];
  } catch (e) {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('mg_cart', JSON.stringify(items));
  }, [items]);

  const add = (product, variation = null, qty = 1) => {
    const price = variation?.price ?? product.price ?? product.minPrice ?? 0;
    const key = `${product.id}-${variation?.id || 'std'}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        { key, id: product.id, name: product.name, price, image: product.image, variation: variation?.label || null, variationId: variation?.id || null, qty },
      ];
    });
    track('add_to_cart', { item: product.name, price, qty });
    toast.success('Added to cart', { description: product.name });
    setIsOpen(true);
  };

  const remove = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
    track('remove_from_cart', { key });
  };

  const setQty = (key, qty) => {
    if (qty <= 0) return remove(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const clear = () => setItems([]);

  const { subtotal, count } = useMemo(() => {
    return items.reduce(
      (acc, i) => ({ subtotal: acc.subtotal + i.price * i.qty, count: acc.count + i.qty }),
      { subtotal: 0, count: 0 }
    );
  }, [items]);

  const value = {
    items,
    add,
    remove,
    setQty,
    clear,
    subtotal,
    count,
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    minOrder: BUSINESS.minOrder,
    meetsMinimum: subtotal >= BUSINESS.minOrder,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
